<template>
  <v-dialog
    v-model="dialog"
    max-width="650"
    content-class="edit-carousel-dialog"
    @keydown.esc="close"
  >
    <v-card class="ecm modal-card">
      <v-btn icon class="close-btn" @click.stop="close"
        ><v-icon>mdi-close</v-icon></v-btn
      >
      <h2 class="modal-title">Редактировать карусель</h2>
      <template v-if="!isSegment">
        <div v-if="isAdmin" class="form-control">
          <div class="field-label">Пользователь</div>
          <v-text-field
            :value="userLabel"
            outlined
            dense
            hide-details
            class="field"
            readonly
          />
        </div>

        <div class="form-control">
          <div class="field-label">Введите название</div>
          <v-text-field
            v-model="form.title"
            outlined
            dense
            hide-details
            class="field"
            placeholder="Карусель от 09.07.2025 16:35"
          />
        </div>

        <div class="form-control">
          <div class="field-label">Статус</div>
          <v-select
            v-model="form.status"
            :items="statuses"
            item-text="label"
            item-value="value"
            outlined
            dense
            hide-details
            class="field"
          />
        </div>
        <div class="form-control">
          <div class="field-label">Скрипт приема входящих звонков</div>
          <v-autocomplete
            v-model="form.scriptId"
            :items="scriptOptions"
            :search-input.sync="scriptQuery"
            :filter="filterScripts"
            item-text="name"
            item-value="id"
            clearable
            outlined
            dense
            hide-details
            class="field"
            placeholder="Начните вводить имя скрипта…"
            :loading="loadingScripts"
            :disabled="loadingScripts"
            attach
            :menu-props="{ offsetY: true, maxHeight: 320 }"
            @focus="ensureScriptsLoaded"
            no-data-text="Скриптов не найдено"
          >
            <template #item="{ item }">
              <div
                style="display: flex; flex-direction: column; line-height: 1.2"
              >
                <span style="font-weight: 600">{{ item.name }}</span>
                <span style="font-size: 12px; opacity: 0.75"
                  >id: {{ item.id }}</span
                >
              </div>
            </template>
          </v-autocomplete>
        </div>
        <v-checkbox
          v-model="form.autorenew"
          hide-details
          class="checkbox"
          color="#4B5EFF"
          :disabled="form.autoreplace"
        >
          <template #label>
            <span class="checkbox-label">
              Автопродление аренды
              <v-tooltip bottom max-width="320">
                <template #activator="{ on, attrs }">
                  <v-icon small class="help-icon" v-bind="attrs" v-on="on"
                    >mdi-help-circle</v-icon
                  >
                </template>
                <span
                  >Продлять аренду номеров по окончании оплаченного
                  периода</span
                >
              </v-tooltip>
            </span>
          </template>
        </v-checkbox>
      </template>
      <template v-if="!isOperatorParent">
        <v-checkbox
          v-model="form.autoreplace"
          hide-details
          class="checkbox"
          color="#4B5EFF"
        >
          <template #label>
            <span class="checkbox-label">
              Автозамена номеров
              <v-tooltip bottom max-width="420">
                <template #activator="{ on, attrs }">
                  <v-icon small class="help-icon" v-bind="attrs" v-on="on"
                    >mdi-help-circle</v-icon
                  >
                </template>
                <span>
                  По расписанию текущие номера будут переноситься в новую
                  карусель в статусе «Только входящие», а в выбранную карусель
                  добавятся новые номера в указанном количестве.
                </span>
              </v-tooltip>
            </span>
          </template>
        </v-checkbox>

        <template v-if="form.autoreplace">
          <div class="form-control">
            <div class="field-label">Тип автозамены</div>

            <v-radio-group
              v-model="form.autoReplaceType"
              class="radio-group"
              mandatory
              hide-details
            >
              <v-radio
                class="radio"
                color="#4B5EFF"
                value="schedule"
                label="По расписанию"
              />

              <v-radio
                class="radio"
                color="#4B5EFF"
                value="threshold"
                label="По порогу звонков с 1 номера"
              />
            </v-radio-group>
          </div>
          <template v-if="form.autoReplaceType === 'schedule'">
            <div v-if="!form.scheduleSeparateDays" class="form-control">
              <div class="sub-label">Расписание автозамены</div>

              <div class="days-input">
                <v-chip-group v-model="form.days" multiple class="days">
                  <v-chip
                    v-for="d in weekdays"
                    :key="d.value"
                    :value="d.value"
                    label
                  >
                    {{ d.label }}
                  </v-chip>
                </v-chip-group>
              </div>
            </div>

            <div class="form-control">
              <v-checkbox
                v-model="form.scheduleSeparateDays"
                hide-details
                class="checkbox"
                color="#4B5EFF"
                label="Настроить дни отдельно"
              />
            </div>

            <template v-if="!form.scheduleSeparateDays">
              <div class="form-control">
                <div class="sub-label">Периодичность</div>

                <v-select
                  v-model="form.scheduleFrequency"
                  :items="frequencyOptions"
                  item-text="label"
                  item-value="value"
                  outlined
                  dense
                  hide-details
                  class="field"
                />
              </div>

              <div
                v-if="form.scheduleFrequency === 'custom'"
                class="schedule-row"
              >
                <div class="form-control">
                  <div class="sub-label">Часов</div>
                  <v-text-field
                    v-model.number="form.scheduleCustomHours"
                    outlined
                    dense
                    hide-details
                    class="field"
                    type="number"
                    min="0"
                    max="12"
                  />
                </div>

                <div class="form-control">
                  <div class="sub-label">Минут</div>
                  <v-text-field
                    v-model.number="form.scheduleCustomMinutes"
                    outlined
                    dense
                    hide-details
                    class="field"
                    type="number"
                    min="0"
                    max="59"
                  />
                </div>
              </div>
              <div
                v-if="customScheduleStepInvalid"
                class="schedule-error schedule-error--compact"
              >
                Интервал должен быть от 10 минут до 12 часов.
              </div>

              <div class="schedule-row">
                <div class="time-col">
                  <div class="sub-label">
                    {{
                      form.scheduleFrequency === 'once'
                        ? 'Время автозамены'
                        : 'Первая автозамена'
                    }}
                  </div>

                  <v-text-field
                    v-model="form.scheduleStartTime"
                    outlined
                    dense
                    hide-details
                    class="time"
                    type="tel"
                    inputmode="numeric"
                    maxlength="5"
                    placeholder="20:00"
                    @input="onScheduleStartTimeInput"
                    @blur="onScheduleStartTimeBlur"
                  >
                    <template #append
                      ><v-icon>mdi-clock-outline</v-icon></template
                    >
                  </v-text-field>
                </div>

                <div v-if="form.scheduleFrequency !== 'once'" class="time-col">
                  <div class="sub-label">Последняя автозамена</div>

                  <v-select
                    v-model="form.scheduleEndTime"
                    :items="availableEndTimes"
                    outlined
                    dense
                    hide-details
                    class="time"
                    :disabled="!availableEndTimes.length"
                  />
                </div>
              </div>
              <div
                v-if="
                  form.scheduleFrequency !== 'once' &&
                  !customScheduleStepInvalid &&
                  !availableEndTimes.length &&
                  form.scheduleStartTime
                "
                class="schedule-error"
              >
                При такой настройке повторная автозамена в этот день невозможна.
                Выберите более раннее время или меньшую периодичность.
              </div>
              <div class="schedule-preview">
                {{ schedulePreview }}
              </div>

              <div class="tz-hint">Время по МСК</div>
            </template>
            <template v-else>
              <div class="day-schedule-list">
                <div class="day-schedule-head">
                  <div>День</div>
                  <div>Периодичность</div>
                  <div>Первая</div>
                  <div>Последняя</div>
                </div>
                <div
                  v-for="d in weekdays"
                  :key="d.value"
                  class="day-schedule-row"
                  :class="{
                    'is-disabled': !form.scheduleDays[d.value].enabled,
                  }"
                >
                  <v-checkbox
                    v-model="form.scheduleDays[d.value].enabled"
                    hide-details
                    dense
                    color="#4B5EFF"
                    :label="d.label"
                  />

                  <v-select
                    v-model="form.scheduleDays[d.value].frequency"
                    :items="dayFrequencyOptions"
                    item-text="label"
                    item-value="value"
                    dense
                    outlined
                    hide-details
                    class="field"
                    :disabled="!form.scheduleDays[d.value].enabled"
                  />

                  <v-text-field
                    v-model="form.scheduleDays[d.value].startTime"
                    dense
                    outlined
                    hide-details
                    class="time day-start-time"
                    type="tel"
                    inputmode="numeric"
                    maxlength="5"
                    :disabled="!form.scheduleDays[d.value].enabled"
                    @input="(v) => onDayStartTimeInput(d.value, v)"
                    @blur="() => onDayStartTimeBlur(d.value)"
                  />

                  <v-select
                    v-if="form.scheduleDays[d.value].frequency !== 'once'"
                    v-model="form.scheduleDays[d.value].endTime"
                    :items="getDayEndTimes(d.value)"
                    dense
                    outlined
                    hide-details
                    class="time"
                    :disabled="
                      !form.scheduleDays[d.value].enabled ||
                      !getDayEndTimes(d.value).length
                    "
                  />
                  <div
                    v-if="
                      form.scheduleDays[d.value].enabled &&
                      form.scheduleDays[d.value].frequency !== 'once' &&
                      form.scheduleDays[d.value].startTime &&
                      !getDayEndTimes(d.value).length
                    "
                    class="day-schedule-error"
                  >
                    Повторная автозамена в этот день невозможна. Выберите более
                    раннее время или меньшую периодичность.
                  </div>
                  <div v-else class="day-empty">—</div>
                  <div class="tz-hint">Время по МСК</div>
                </div>
              </div>
            </template>
          </template>
          <template v-if="form.autoReplaceType === 'threshold'">
            <div class="form-control">
              <div class="field-label">Порог звонков с 1 номера</div>

              <v-text-field
                v-model.number="form.autoCallsThreshold"
                outlined
                dense
                hide-details
                class="field"
                type="tel"
                inputmode="numeric"
                pattern="\d*"
                maxlength="5"
                @keydown="onlyDigits"
                @input="onThresholdInput"
                @blur="onThresholdBlur"
              />
            </div>

            <div class="threshold-warning">
              Для выбранной автозамены срок аренды номеров сокращается до 3
              дней.
            </div>
          </template>

          <div class="form-control">
            <div class="field-label">Источник номеров</div>
            <v-radio-group
              v-model="form.autoSource"
              class="radio-group"
              mandatory
              hide-details
            >
              <v-radio
                class="radio"
                color="#4B5EFF"
                value="regular"
                label="Обычные номера"
              />
              <v-radio
                class="radio"
                color="#4B5EFF"
                value="reserve"
                label="Номера из активного резерва"
              />
            </v-radio-group>
          </div>

          <div v-if="form.autoSource === 'reserve'" class="form-control">
            <div class="field-label">Резерв (статус «Активен»)</div>
            <v-select
              v-model="form.autoReserveId"
              :items="reserveOptions"
              item-text="label"
              item-value="id"
              dense
              outlined
              hide-details
              class="field"
              placeholder="Резерв…"
              :loading="loadingReserves"
              :disabled="loadingReserves"
              attach
              :menu-props="{ offsetY: true, maxHeight: 320 }"
              @focus="loadActiveReserves"
            >
              <template #item="{ item }">
                <div
                  class="reserve-item"
                  :class="{ 'is-empty': (item.available || 0) <= 0 }"
                >
                  <div class="reserve-title">«{{ item.name }}»</div>
                  <div class="reserve-meta">
                    <span v-if="item.marking" class="reserve-mark"
                      >Маркировка: {{ item.marking }}</span
                    >
                    <span class="reserve-available"
                      >доступно: {{ item.available }}</span
                    >
                  </div>
                </div>
              </template>
              <template #selection="{ item }">
                <div class="reserve-selection">
                  <span>«{{ item.name }}»</span>
                  <span v-if="item.marking" class="reserve-selection-mark">
                    · {{ item.marking }}</span
                  >
                </div>
              </template>
            </v-select>
          </div>

          <div class="form-control">
            <div class="field-label">
              Количество номеров
              <span
                v-if="form.autoSource === 'reserve' && form.autoReserveId"
                class="muted"
              >
                (доступно: {{ reserveLeft }})
              </span>
            </div>
            <v-text-field
              v-model.number="form.amount"
              :readonly="isSegment"
              outlined
              dense
              hide-details
              class="field"
              type="tel"
              inputmode="numeric"
              pattern="\d*"
              maxlength="5"
              @keydown="onlyDigits"
              @paste.prevent="onAmountPaste"
              @input="onAutoAmountInput"
              @blur="onAmountBlur"
            />
          </div>

          <div class="price-box">
            <div
              v-if="loadingPrice && form.autoSource !== 'reserve'"
              class="price-loader-wrap"
              role="status"
              aria-label="Загрузка стоимости"
            >
              <span class="price-loader"></span>
            </div>
            <div v-else class="price-center">
              <div class="price-label">
                {{
                  form.autoSource === 'reserve'
                    ? 'Оплата'
                    : 'Со счёта будет списываться:'
                }}
              </div>
              <div class="price-value">
                <span v-if="priceError && form.autoSource !== 'reserve'">
                  {{ priceError }}
                </span>
                <template v-else>
                  {{ (form.autoSource === 'reserve' ? 0 : total) | rub }}
                </template>
                <span v-if="form.autoSource === 'reserve'" class="muted">
                  — из резерва</span
                >
              </div>
            </div>
          </div>
        </template>
      </template>

      <v-btn
        block
        height="56"
        class="yellow-btn mt-7"
        :disabled="!isValid"
        @click="save"
      >
        СОХРАНИТЬ
      </v-btn>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'EditCarouselModal',
  props: {
    value: { type: Boolean, required: true },
    carousel: { type: Object, default: () => ({}) },
    isAdmin: { type: Boolean, default: false },
  },
  emits: ['input', 'save'],
  data() {
    return {
      dialog: false,

      trunkPrices: [],
      segmentPrice: null,
      loadingPrice: false,
      priceError: '',
      priceRequestId: 0,

      isHydrating: false,

      statuses: [
        { label: 'Активна', value: 'active' },
        { label: 'Только входящие', value: 'in' },
      ],
      weekdays: [
        { label: 'Пн', value: 'mon' },
        { label: 'Вт', value: 'tue' },
        { label: 'Ср', value: 'wed' },
        { label: 'Чт', value: 'thu' },
        { label: 'Пт', value: 'fri' },
        { label: 'Сб', value: 'sat' },
        { label: 'Вс', value: 'sun' },
      ],

      form: {
        title: '',
        status: 'active',
        autorenew: false,
        autoreplace: true,
        days: ['mon', 'tue', 'wed', 'thu', 'fri'],
        time: '20:00',
        scheduleSeparateDays: false,
        scheduleFrequency: 'once',
        scheduleStartTime: '20:00',
        scheduleEndTime: '',
        scheduleCustomHours: 0,
        scheduleCustomMinutes: 30,
        scheduleDays: {
          mon: {
            enabled: true,
            frequency: 'once',
            startTime: '20:00',
            endTime: '',
            customHours: 0,
            customMinutes: 30,
          },
          tue: {
            enabled: true,
            frequency: 'once',
            startTime: '20:00',
            endTime: '',
            customHours: 0,
            customMinutes: 30,
          },
          wed: {
            enabled: true,
            frequency: 'once',
            startTime: '20:00',
            endTime: '',
            customHours: 0,
            customMinutes: 30,
          },
          thu: {
            enabled: true,
            frequency: 'once',
            startTime: '20:00',
            endTime: '',
            customHours: 0,
            customMinutes: 30,
          },
          fri: {
            enabled: true,
            frequency: 'once',
            startTime: '20:00',
            endTime: '',
            customHours: 0,
            customMinutes: 30,
          },
          sat: {
            enabled: false,
            frequency: 'once',
            startTime: '20:00',
            endTime: '',
            customHours: 0,
            customMinutes: 30,
          },
          sun: {
            enabled: false,
            frequency: 'once',
            startTime: '20:00',
            endTime: '',
            customHours: 0,
            customMinutes: 30,
          },
        },
        amount: 500,
        autoSource: 'regular',
        autoReserveId: null,
        scriptId: null,
        autoReplaceType: 'schedule',
        autoCallsThreshold: 1000,
      },
      frequencyOptions: [
        { label: 'Один раз в день', value: 'once' },
        { label: 'Каждые 30 минут', value: '30m' },
        { label: 'Каждый час', value: '1h' },
        { label: 'Каждые 2 часа', value: '2h' },
        { label: 'Другая периодичность', value: 'custom' },
      ],
      dayFrequencyOptions: [
        { label: 'Один раз в день', value: 'once' },
        { label: 'Каждые 30 минут', value: '30m' },
        { label: 'Каждый час', value: '1h' },
        { label: 'Каждые 2 часа', value: '2h' },
      ],

      reserveOptions: [],
      loadingReserves: false,
      reserveLeft: 0,
      scriptOptions: [],
      loadingScripts: false,
      scriptQuery: '',
      scriptsLoadedFor: '',

      userLabel: '',
    };
  },
  computed: {
    isSegment() {
      return Boolean(this.carousel?.segment);
    },

    isOperatorParent() {
      return (
        this.carousel?.rowType === 'parent' &&
        this.carousel?.carouselType === 'operator'
      );
    },
    total() {
      const amount = Math.max(0, Number(this.form.amount) || 0);
      if (this.isSegment) {
        return this.segmentPrice === null
          ? 0
          : Math.ceil(this.segmentPrice * amount);
      }
      const prices = Array.isArray(this.trunkPrices) ? this.trunkPrices : [];

      if (!prices.length || amount <= 0) return 0;

      const baseCount = Math.floor(amount / prices.length);
      const remainder = amount % prices.length;

      let result = 0;

      prices.forEach((item, index) => {
        const allocatedCount = baseCount + (index < remainder ? 1 : 0);
        const trunkPrice = Math.max(0, Number(item.price) || 0);

        result += trunkPrice * allocatedCount;
      });

      return Math.ceil(result);
    },
    availableEndTimes() {
      const start = this.form.scheduleStartTime;
      if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(start || '')) return [];

      const step = this.getScheduleStepMinutes();
      if (!step) return [];

      const [h, m] = start.split(':').map(Number);
      const startMinutes = h * 60 + m;
      const result = [];

      for (
        let minutes = startMinutes + step;
        minutes < 24 * 60;
        minutes += step
      ) {
        const hh = Math.floor(minutes / 60);
        const mm = minutes % 60;
        result.push(
          `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`,
        );
      }

      return result;
    },
    isValid() {
      if (!this.form.title) return false;
      if (!this.form.autoreplace) return true;
      if (this.form.autoSource !== 'reserve' && (this.loadingPrice || this.priceError)) {
        return false;
      }
      if (this.form.autoReplaceType === 'threshold') {
        const threshold = Number(this.form.autoCallsThreshold || 0);
        const amountNum = Number(this.form.amount) || 0;
        if (threshold < 10) return false;
        if (this.form.autoSource === 'reserve') {
          const hasReserve = !!this.form.autoReserveId;
          const left = Math.max(0, Number(this.reserveLeft) || 0);
          return hasReserve && amountNum >= 1 && amountNum <= left;
        }
        return amountNum >= 1 && amountNum <= 10000;
      }

      const startTimeOk = /^([01]\d|2[0-3]):[0-5]\d$/.test(
        this.form.scheduleStartTime || '',
      );
      const endTimeOk =
        this.form.scheduleFrequency === 'once' ||
        this.availableEndTimes.includes(this.form.scheduleEndTime);
      const amountNum = Number(this.form.amount) || 0;

      if (this.form.scheduleSeparateDays) {
        const rows = Object.values(this.form.scheduleDays || {});
        const enabledRows = rows.filter((r) => r.enabled);

        if (!enabledRows.length) return false;

        const rowsOk = Object.entries(this.form.scheduleDays || {})
          .filter(([, r]) => r.enabled)
          .every(([day, r]) => {
            const startOk = /^([01]\d|2[0-3]):[0-5]\d$/.test(r.startTime || '');
            const endOk =
              r.frequency === 'once' ||
              this.getDayEndTimes(day).includes(r.endTime);

            return startOk && endOk;
          });

        if (!rowsOk) return false;

        if (this.form.autoSource === 'reserve') {
          const hasReserve = !!this.form.autoReserveId;
          const left = Math.max(0, Number(this.reserveLeft) || 0);
          return hasReserve && amountNum >= 1 && amountNum <= left;
        }

        return amountNum >= 1 && amountNum <= 10000;
      }

      if (this.form.autoSource === 'reserve') {
        const hasReserve = !!this.form.autoReserveId;
        const left = Math.max(0, Number(this.reserveLeft) || 0);
        return (
          this.form.days?.length > 0 &&
          startTimeOk &&
          endTimeOk &&
          hasReserve &&
          amountNum >= 1 &&
          amountNum <= left
        );
      }
      return (
        this.form.days?.length > 0 &&
        startTimeOk &&
        endTimeOk &&
        amountNum >= 1 &&
        amountNum <= 10000
      );
    },
    schedulePreview() {
      if (!this.form.days?.length)
        return 'Выберите хотя бы один день автозамены.';

      if (this.form.scheduleFrequency === 'once') {
        return `В выбранные дни автозамена выполнится один раз: в ${this.form.scheduleStartTime}.`;
      }

      //  if (this.customScheduleStepInvalid) {
      //    return 'Укажите корректную периодичность: от 10 минут до 12 часов.'
      //   }

      if (!this.form.scheduleEndTime) {
        return 'Выберите время последней автозамены.';
      }

      const times = [this.form.scheduleStartTime];
      const endIndex = this.availableEndTimes.indexOf(
        this.form.scheduleEndTime,
      );

      if (endIndex >= 0) {
        times.push(...this.availableEndTimes.slice(0, endIndex + 1));
      }

      return `В выбранные дни автозамена выполнится ${times.length} раз: ${times
        .slice(0, 3)
        .join(', ')}${
        times.length > 3 ? ' ... ' + times[times.length - 1] : ''
      }.`;
    },
    customScheduleStepInvalid() {
      if (this.form.scheduleFrequency !== 'custom') return false;

      const hours = Number(this.form.scheduleCustomHours) || 0;
      const minutes = Number(this.form.scheduleCustomMinutes) || 0;
      const total = hours * 60 + minutes;

      return total < 10 || total > 720;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(v) {
        this.dialog = v;
      },
    },
    dialog(v) {
      this.$emit('input', v);
      if (v) this.loadPrice(this.carousel?.user || this.carousel?.userId);
      this.ensureScriptsLoaded();
      if (
        v &&
        this.form.autoSource === 'reserve' &&
        !this.reserveOptions.length
      ) {
        this.loadActiveReserves();
      }
    },

    carousel: {
      immediate: true,
      deep: true,
      async handler(c) {
        if (!c) return;
        this.isHydrating = true;

        const amountRaw = c.amount ?? c.numbersCount;
        const amountNum = Number.isFinite(Number(amountRaw))
          ? Number(amountRaw)
          : Number(this.form.amount) || 0;

        const autoSchedule = this.parseAutoSchedule(c.auto_schedule_json);
        const defaultScheduleDays = this.buildDefaultScheduleDays();

        this.form = {
          title: (c.title ?? c.carousel ?? '').trim() || this.form.title,
          status:
            c.status ??
            (c.carouselStatus === 'Активна' ? 'active' : c.status || 'active'),
          autorenew: !!(
            c.autorenew ??
            String(c.autoRenew || '')
              .toLowerCase()
              .includes('включ')
          ),
          autoreplace: !!(c.autoreplace ?? c.autoReplaceOn ?? false),
          days:
            Array.isArray(autoSchedule?.days) && autoSchedule.days.length
              ? autoSchedule.days
              : Array.isArray(c.days) && c.days.length
              ? c.days
              : this.form.days,
          time: typeof c.time === 'string' && c.time ? c.time : this.form.time,
          scheduleSeparateDays: autoSchedule?.mode === 'by_days',
          scheduleFrequency: autoSchedule?.frequency || 'once',
          scheduleStartTime: autoSchedule?.startTime || c.time || '20:00',
          scheduleEndTime: autoSchedule?.endTime || '',
          scheduleCustomHours: Number(autoSchedule?.customHours ?? 0),
          scheduleCustomMinutes: Number(autoSchedule?.customMinutes ?? 30),
          scheduleDays: autoSchedule?.daysConfig || defaultScheduleDays,
          amount: amountNum,

          autoSource: c.auto_source ?? c.autoSource ?? 'regular',
          autoReserveId: c.auto_reserve_id ?? c.autoReserveId ?? null,
          scriptId: c.incoming_script_id ?? c.scriptId ?? null,

          autoReplaceType:
            c.auto_replace_type || c.autoReplaceType || 'schedule',
          autoCallsThreshold: Number(
            c.auto_calls_threshold || c.autoCallsThreshold || 1000,
          ),
        };

        this.userLabel = String(c.user || c.userId || '').trim();

        if (this.dialog) this.loadPrice(c.user || c.userId);

        this.$nextTick(() => {
          this.isHydrating = false;
          if (this.dialog) this.ensureScriptsLoaded();
          if (this.form.autoSource === 'reserve') this.loadActiveReserves();
        });
      },
    },

    'form.autoSource'(val) {
      if (this.isHydrating) return;
      if (val === 'reserve') {
        if (!this.reserveOptions.length) this.loadActiveReserves();
      } else {
        this.reserveLeft = 0;
      }
    },

    'form.autoReserveId'(newVal) {
      if (this.isHydrating) return;
      const r = this.reserveOptions.find(
        (x) => String(x.id) === String(newVal),
      );
      this.reserveLeft = r ? Number(r.available || 0) : 0;
      if (
        this.form.autoSource === 'reserve' &&
        this.reserveLeft > 0 &&
        Number(this.form.amount) > this.reserveLeft
      ) {
        this.form.amount = this.reserveLeft;
      }
    },
    'form.autoreplace'(val) {
      if (val) {
        this.form.autorenew = false;
      }
    },

    'form.autorenew'(val) {
      if (val) {
        this.form.autoreplace = false;
      }
    },
    'form.scheduleFrequency'() {
      if (this.isHydrating) return;
      this.form.scheduleEndTime = '';
    },

    'form.scheduleCustomHours'() {
      if (this.isHydrating) return;
      this.form.scheduleEndTime = '';
    },

    'form.scheduleCustomMinutes'() {
      if (this.isHydrating) return;
      this.form.scheduleEndTime = '';
    },
    'form.scheduleSeparateDays'(val) {
      if (this.isHydrating) return;

      if (val) {
        this.form.scheduleDays = this.buildDefaultScheduleDays();
      }
    },
  },
  methods: {
    getDayStepMinutes(day) {
      const row = this.form.scheduleDays[day];
      if (!row) return 0;

      if (row.frequency === '30m') return 30;
      if (row.frequency === '1h') return 60;
      if (row.frequency === '2h') return 120;

      return 0;
    },

    getDayEndTimes(day) {
      const row = this.form.scheduleDays[day];
      if (!row) return [];

      const start = row.startTime;
      if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(start || '')) return [];

      const step = this.getDayStepMinutes(day);
      if (!step) return [];

      const [h, m] = start.split(':').map(Number);
      const startMinutes = h * 60 + m;
      const result = [];

      for (
        let minutes = startMinutes + step;
        minutes < 24 * 60;
        minutes += step
      ) {
        const hh = Math.floor(minutes / 60);
        const mm = minutes % 60;
        result.push(
          `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`,
        );
      }

      return result;
    },
    parseAutoSchedule(raw) {
      if (!raw) return null;

      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (!parsed || typeof parsed !== 'object') return null;
        return parsed;
      } catch {
        return null;
      }
    },
    buildDefaultScheduleDays() {
      const result = {};

      this.weekdays.forEach((d) => {
        const enabled = this.form.days.includes(d.value);

        result[d.value] = {
          enabled,
          frequency: this.form.scheduleFrequency,
          startTime: this.form.scheduleStartTime,
          endTime: this.form.scheduleEndTime,
          customHours: this.form.scheduleCustomHours,
          customMinutes: this.form.scheduleCustomMinutes,
        };
      });

      return result;
    },
    onThresholdInput(v) {
      const digits = String(v ?? '').replace(/\D/g, '');
      if (digits === '') {
        this.form.autoCallsThreshold = '';
        return;
      }

      this.form.autoCallsThreshold = Number(digits);
    },

    onThresholdBlur() {
      let n = Number(this.form.autoCallsThreshold || 0);

      if (!n || n < 10) n = 10;

      this.form.autoCallsThreshold = n;
    },
    filterScripts(item, queryText) {
      const q = (queryText || '').toLowerCase().trim();
      if (!q) return true;
      const name = (item.name || '').toLowerCase();
      const id = (item.id || '').toLowerCase();
      return name.includes(q) || id.includes(q);
    },
    ensureScriptsLoaded() {
      const u = String(
        this.carousel?.user || this.carousel?.userId || '',
      ).replace(/\D/g, '');
      const key = this.isAdmin && u.length === 11 ? u : 'self';
      if (this.scriptsLoadedFor === key && this.scriptOptions.length) return;
      this.loadScripts();
    },

    async loadScripts() {
      if (this.loadingScripts) return;
      this.loadingScripts = true;
      try {
        const body = {};
        const u = String(
          this.carousel?.user || this.carousel?.userId || '',
        ).replace(/\D/g, '');
        if (this.isAdmin && u.length === 11) body.user = u;

        const resp = await new Promise((resolve) =>
          this.$http.post('/numbers/scripts/list', body, (r) => resolve(r)),
        );
        const arr = Array.isArray(resp) ? resp : [];
        this.scriptOptions = arr
          .map((s) => ({
            id: s.id || s.script_id || s.key || '',
            name: s.name || s.title || s.id || s.script_id || '',
          }))
          .filter((x) => x.id);

        // ⬇️ добавим выбранный id, если его нет в списке
        if (
          this.form.scriptId &&
          !this.scriptOptions.some(
            (x) => String(x.id) === String(this.form.scriptId),
          )
        ) {
          this.scriptOptions.unshift({
            id: this.form.scriptId,
            name: String(this.form.scriptId),
          });
        }

        this.scriptsLoadedFor = this.isAdmin && u.length === 11 ? u : 'self';
      } finally {
        this.loadingScripts = false;
      }
    },
    close() {
      this.dialog = false;
    },

    onlyDigits(e) {
      const allow = [
        'Backspace',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'Tab',
        'Home',
        'End',
        'Enter',
      ];
      if (allow.includes(e.key) || e.ctrlKey || e.metaKey) return;
      if (!/^\d$/.test(e.key)) e.preventDefault();
    },
    onAmountPaste(e) {
      const txt =
        (e.clipboardData || window.clipboardData).getData('text') || '';
      const digits = txt.replace(/\D/g, '').slice(0, 5);
      let n = Math.min(10000, Number(digits || 0));
      if (!n) n = 1;
      if (this.form.autoSource === 'reserve') {
        const cap = Math.max(0, Number(this.reserveLeft) || 0);
        if (cap > 0) n = Math.min(n, cap);
      }
      this.form.amount = n;
    },
    onAutoAmountInput(v) {
      const digits = String(v ?? '').replace(/\D/g, '');
      if (digits === '') {
        this.form.amount = '';
        return;
      }
      let n = Math.min(10000, Number(digits));
      if (this.form.autoSource === 'reserve') {
        const cap = Math.max(0, Number(this.reserveLeft) || 0);
        if (cap > 0) n = Math.min(n, cap);
      }
      this.form.amount = n;
    },
    onAmountBlur() {
      let n = Number(this.form.amount || 0);
      if (!n) n = 1;
      n = Math.max(1, Math.min(10000, n));
      if (this.form.autoSource === 'reserve') {
        const cap = Math.max(0, Number(this.reserveLeft) || 0);
        if (cap > 0) n = Math.min(n, cap);
      }
      this.form.amount = n;
    },

    getScheduleStepMinutes() {
      if (this.form.scheduleFrequency === '30m') return 30;
      if (this.form.scheduleFrequency === '1h') return 60;
      if (this.form.scheduleFrequency === '2h') return 120;

      if (this.form.scheduleFrequency === 'custom') {
        const hours = Number(this.form.scheduleCustomHours) || 0;
        const minutes = Number(this.form.scheduleCustomMinutes) || 0;
        const total = hours * 60 + minutes;

        if (total < 10 || total > 720) return 0;

        return total;
      }

      return 0;
    },

    maskTime(v) {
      const d = String(v || '')
        .replace(/\D/g, '')
        .slice(0, 4);
      if (d.length <= 2) return d;
      return `${d.slice(0, 2)}:${d.slice(2)}`;
    },

    normalizeTime(v) {
      let t = (v || '').trim();
      if (!t) return '';

      if (/^\d{1,2}$/.test(t)) t = t.padStart(2, '0') + ':00';

      if (/^\d{3,4}$/.test(t)) {
        const s = t.padStart(4, '0');
        t = `${s.slice(0, 2)}:${s.slice(2)}`;
      }

      if (/^\d{2}:\d$/.test(t)) t = t + '0';
      if (!/^\d{2}:\d{2}$/.test(t)) return v;

      let [h, m] = t.split(':').map((n) => Number(n));
      h = Math.max(0, Math.min(23, h));
      m = Math.max(0, Math.min(59, m));

      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    },

    onScheduleStartTimeInput(v) {
      this.form.scheduleStartTime = this.maskTime(v);
      this.form.scheduleEndTime = '';
    },
    onDayStartTimeInput(day, v) {
      this.form.scheduleDays[day].startTime = this.maskTime(v);
      this.form.scheduleDays[day].endTime = '';
    },

    onDayStartTimeBlur(day) {
      this.form.scheduleDays[day].startTime = this.normalizeTime(
        this.form.scheduleDays[day].startTime,
      );
      this.form.scheduleDays[day].endTime = '';
    },

    onDayEndTimeInput(day, v) {
      this.form.scheduleDays[day].endTime = this.maskTime(v);
    },

    onDayEndTimeBlur(day) {
      this.form.scheduleDays[day].endTime = this.normalizeTime(
        this.form.scheduleDays[day].endTime,
      );
    },

    onScheduleStartTimeBlur() {
      this.form.scheduleStartTime = this.normalizeTime(
        this.form.scheduleStartTime,
      );
      this.form.scheduleEndTime = '';
    },

    onTimeMaskedInput(v) {
      const d = String(v || '')
        .replace(/\D/g, '')
        .slice(0, 4);
      if (d.length <= 2) this.form.time = d;
      else this.form.time = `${d.slice(0, 2)}:${d.slice(2)}`;
    },
    onTimeBlur() {
      let t = (this.form.time || '').trim();
      if (!t) return;
      if (/^\d{1,2}$/.test(t)) t = t.padStart(2, '0') + ':00';
      if (/^\d{3,4}$/.test(t)) {
        const s = t.padStart(4, '0');
        t = `${s.slice(0, 2)}:${s.slice(2)}`;
      }
      if (/^\d{2}:\d$/.test(t)) t = t + '0';
      if (!/^\d{2}:\d{2}$/.test(t)) return;
      let [h, m] = t.split(':').map((n) => Number(n));
      h = Math.max(0, Math.min(23, h));
      m = Math.max(0, Math.min(59, m));
      this.form.time = `${String(h).padStart(2, '0')}:${String(m).padStart(
        2,
        '0',
      )}`;
    },

    async loadPrice(userLike) {
      const requestId = ++this.priceRequestId;
      this.loadingPrice = true;
      this.trunkPrices = [];
      this.segmentPrice = null;
      this.priceError = '';
      try {
        const body = {};
        const u = String(userLike || '').replace(/\D/g, '');
        if (this.isAdmin && u.length === 11) body.user = u;
        const isSegment = this.isSegment;
        if (isSegment) {
          body.carouselId = String(this.carousel.id || this.carousel.carouselId || '');
          body.segment = String(this.carousel.segment);
          if (!body.carouselId) throw new Error('Не удалось определить карусель');
        }
        const resp = await new Promise((resolve) =>
          this.$http.post('/numbers/price_per_number', body, (r) => resolve(r)),
        );
        if (requestId !== this.priceRequestId) return;
        if (resp?.ok === false) throw new Error('Не удалось загрузить стоимость');
        if (isSegment) {
          const price = resp?.segmentPrice;
          if (price === null || price === undefined || String(price).trim() === '' ||
              !Number.isFinite(Number(price)) || Number(price) < 0) {
            throw new Error('Не удалось загрузить стоимость сегмента');
          }
          this.segmentPrice = Number(price);
        } else if (!Array.isArray(resp?.prices) || !resp.prices.length) {
          throw new Error('Не удалось загрузить стоимость');
        }
        this.trunkPrices = Array.isArray(resp?.prices) ? resp.prices : [];
      } catch (error) {
        if (requestId === this.priceRequestId) {
          this.priceError = error.message || 'Не удалось загрузить стоимость';
        }
      } finally {
        if (requestId === this.priceRequestId) this.loadingPrice = false;
      }
    },

    async loadActiveReserves() {
      if (this.loadingReserves) return;
      this.loadingReserves = true;
      try {
        const body = { status: 'active' };
        const u = String(
          this.carousel?.user || this.carousel?.userId || '',
        ).replace(/\D/g, '');
        if (this.isAdmin && u.length === 11) body.user = u;

        const resp = await new Promise((resolve) =>
          this.$http.post('/numbers/reserves/list', body, (r) => resolve(r)),
        );
        const items = Array.isArray(resp) ? resp : [];
        this.reserveOptions = items
          .map((x) => {
            const id = x.id;
            const available = Number(x.available || x.availableCount || 0);
            const name =
              x.title || x.reserve || `Резерв #${String(id).slice(0, 8)}`;
            const marking = x.marking || '';
            return {
              id,
              available,
              name,
              marking,
              label: `${name}${
                marking ? ` — ${marking}` : ''
              } — доступно: ${available}`,
            };
          })
          .filter((i) => i.id);
        if (this.form.autoReserveId) {
          const r = this.reserveOptions.find(
            (x) => String(x.id) === String(this.form.autoReserveId),
          );
          const left = r ? Number(r.available || 0) : 0;
          this.reserveLeft = left;
          if (
            this.form.autoSource === 'reserve' &&
            left > 0 &&
            Number(this.form.amount) > left
          ) {
            this.form.amount = left;
          }
        }
      } finally {
        this.loadingReserves = false;
      }
    },

    save() {
      this.onAmountBlur();

      if (this.form.autoReplaceType === 'threshold') {
        this.onThresholdBlur();
      }
      if (!this.isValid) return;

      const autoSchedule = {
        mode: this.form.scheduleSeparateDays ? 'by_days' : 'base',
        frequency: this.form.scheduleFrequency,
        days: this.form.days,
        time:
          this.form.scheduleFrequency === 'once'
            ? this.form.scheduleStartTime
            : '',
        startTime: this.form.scheduleStartTime,
        endTime: this.form.scheduleEndTime,
        customHours: Number(this.form.scheduleCustomHours) || 0,
        customMinutes: Number(this.form.scheduleCustomMinutes) || 0,
        daysConfig: this.form.scheduleDays || {},
      };

      this.$emit('save', {
        title: this.form.title,
        status: this.form.status,
        autorenew: !!this.form.autorenew,
        autoreplace: !!this.form.autoreplace,
        days: Array.isArray(this.form.days) ? this.form.days : [],
        time: this.form.scheduleStartTime,
        amount: Number(this.form.amount) || 0,
        auto_source: this.form.autoSource,
        auto_reserve_id: this.form.autoReserveId,
        incoming_script_id: this.form.scriptId || '',
        auto_replace_type: this.form.autoReplaceType,
        auto_calls_threshold: Number(this.form.autoCallsThreshold) || 10,
        auto_schedule_json: JSON.stringify(autoSchedule),
      });

      this.close();
    },
  },
  filters: {
    rub(v) {
      const n = Number(v) || 0;
      try {
        return new Intl.NumberFormat('ru-RU').format(n) + ' ₽';
      } catch {
        return n + ' ₽';
      }
    },
  },
};
</script>

<style>
.threshold-warning {
  margin-top: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fff7df;
  color: #7a5200;
  font-size: 13px;
  line-height: 1.35;
}
.edit-carousel-dialog {
  overflow-y: hidden !important;
}

.edit-carousel-dialog .ecm.modal-card {
  max-height: calc(100vh - 64px);
  overflow-y: auto;
}

.edit-carousel-dialog .v-dialog {
  margin: 16px;
}

@media (max-width: 600px) {
  .edit-carousel-dialog .v-dialog {
    margin: 8px;
  }
  .edit-carousel-dialog .ecm.modal-card {
    max-height: calc(100vh - 32px);
  }
}
.ecm {
  --tz-text: #2b2d33;
  --tz-muted: #808185;
  --tz-line: #d9dade;
  --tz-bg: #f8f8f8;
  --tz-accent: #4b5eff;
}
.edit-carousel-dialog {
  overflow: visible;
}
.ecm.modal-card {
  padding: 32px 40px 40px;
  border-radius: 8px;
}
.ecm .modal-title {
  margin: 0 0 24px;
  font: 700 26px/1.3 Manrope, sans-serif;
  text-align: center;
  color: var(--tz-text);
}
.ecm .close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  min-width: 32px;
  height: 32px;
}
.ecm .close-btn .v-icon {
  font-size: 22px;
  color: #808185;
}
.ecm .form-control {
  margin-top: 20px;
}
.ecm .field-label,
.ecm .sub-label {
  text-align: left !important;
}
.ecm .field-label {
  font: 600 13px/1.1 Manrope, sans-serif;
  color: var(--tz-text);
  margin-bottom: 8px;
}
.ecm .field .v-input__slot {
  min-height: 44px;
  border-radius: 8px;
  border-color: var(--tz-line);
}
.ecm .field .v-label {
  display: none;
}
.ecm .field input,
.ecm .field .v-select__selections {
  font: 500 14px/1.2 Manrope, sans-serif;
  color: var(--tz-text);
}
.ecm .field .v-input--is-focused .v-input__slot,
.ecm .field .v-input__slot:hover {
  border-color: var(--tz-accent) !important;
}
.ecm .form-control + .checkbox {
  margin-top: 24px !important;
}
.ecm .checkbox + .checkbox {
  margin-top: 16px !important;
}
.ecm .checkbox-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.ecm .help-icon {
  color: #c8cad0 !important;
}
.ecm .schedule-row {
  display: grid;
  grid-template-columns: 1fr 168px;
  column-gap: 16px;
  align-items: start;
  margin-top: 24px;
}
.ecm .days-input {
  min-height: 40px;
  border: 1px solid var(--tz-line);
  border-radius: 8px;
  padding: 4px 6px;
  display: flex;
  align-items: center;
  overflow: hidden;
}
.ecm .days {
  margin: 0;
  display: flex;
  flex-wrap: nowrap;
}
.ecm .days .v-chip {
  height: 28px;
  padding: 0 6px;
  font-size: 12px;
  line-height: 1;
  border-radius: 6px;
  margin-right: 6px;
  background: #f2f3f5;
  color: #6f7075;
  font-weight: 700;
}
.ecm .days .v-chip:last-child {
  margin-right: 0;
}
.ecm .days .v-chip.v-chip--active {
  background: var(--tz-accent) !important;
  color: #fff !important;
}
.ecm .time {
  margin: 0;
  width: 168px;
}
.ecm .time .v-input__slot {
  min-height: 44px;
  border-radius: 8px;
  border-color: var(--tz-line);
}
.ecm .time input {
  font-variant-numeric: tabular-nums;
}
.ecm .time .v-input--is-focused .v-input__slot,
.ecm .time .v-input__slot:hover {
  border-color: var(--tz-accent) !important;
}
.ecm .price-box {
  background: #fff;
  border: 1px solid var(--tz-line);
  border-radius: 8px;
  padding: 18px 22px;
  margin-top: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.ecm .price-label {
  font: 500 13px/1.2 Manrope, sans-serif;
  color: var(--tz-muted);
}
.ecm .price-value {
  font: 800 20px/1.3 Manrope, sans-serif;
  color: var(--tz-text);
}
.ecm .price-center {
  text-align: center;
}
.ecm .price-loader-wrap {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60px;
}
.ecm .price-loader {
  width: 100%;
  height: 4.8px;
  display: block;
  position: relative;
  overflow: hidden;
}
.ecm .price-loader::after {
  content: '';
  width: 96px;
  height: 4.8px;
  background: #4b5eff;
  position: absolute;
  top: 0;
  left: 0;
  box-sizing: border-box;
  animation: editCarouselPriceLoading 0.6s ease-in-out infinite alternate;
}
@keyframes editCarouselPriceLoading {
  0% {
    left: 0;
    transform: translateX(-1%);
  }
  100% {
    left: 100%;
    transform: translateX(-99%);
  }
}
.ecm .yellow-btn {
  background: var(--tz-accent) !important;
  color: #fff !important;
  font: 800 16px/1.2 Manrope, sans-serif;
  border-radius: 8px;
}
.ecm .checkbox-label {
  color: #2b2d33;
  font-weight: 600;
}
.ecm .help-icon {
  color: #9ea0a6 !important;
}
.ecm .tz-hint {
  margin-top: 6px;
  font: 600 11px/1.1 Manrope, sans-serif;
  color: #6f7075;
  white-space: nowrap;
  opacity: 0.9;
}

/* Резервы — оформление пунктов и выбранного значения */
.reserve-item {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
  padding: 4px 0;
}
.reserve-item.is-empty {
  opacity: 0.6;
}
.reserve-title {
  font-weight: 700;
}
.reserve-meta {
  font-size: 12px;
  color: #7b7d83;
  display: flex;
  gap: 10px;
}
.reserve-selection {
  display: flex;
  align-items: center;
  gap: 6px;
}
.reserve-selection-mark {
  color: #7b7d83;
  font-weight: 600;
}
.muted {
  color: #808185;
}
.ecm .schedule-preview {
  margin-top: 10px;
  font-size: 13px;
  line-height: 1.35;
  color: #6f7075;
}
.ecm .schedule-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 168px;
  gap: 16px;
  align-items: end;
  margin-top: 16px;
}

.ecm .schedule-preview {
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid #e6e7eb;
  border-radius: 8px;
  background: #f8f9fb;
  font-size: 13px;
  line-height: 1.35;
  color: #5f626a;
}

.ecm .day-schedule-list {
  margin-top: 16px;
  border: 1px solid var(--tz-line);
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.ecm .day-schedule-head,
.ecm .day-schedule-row {
  display: grid;
  grid-template-columns: 78px minmax(170px, 1fr) 120px 120px;
  gap: 10px;
  align-items: center;
}

.ecm .day-schedule-head {
  padding: 10px 12px;
  background: #f5f6f8;
  color: #6f7075;
  font-size: 12px;
  font-weight: 700;
}

.ecm .day-schedule-row {
  padding: 10px 12px;
  border-top: 1px solid #eceef2;
}

.ecm .day-schedule-row .v-input {
  margin-top: 0;
}

.ecm .day-schedule-row .v-input__slot {
  min-height: 38px !important;
}

.ecm .day-schedule-row .time {
  width: 116px;
}

.ecm .day-empty {
  height: 38px;
  display: flex;
  align-items: center;
  color: #9ea0a6;
  font-weight: 700;
}

@media (max-width: 600px) {
  .ecm.modal-card {
    padding: 24px 18px 28px;
  }

  .ecm .schedule-row {
    grid-template-columns: 1fr;
  }

  .ecm .time {
    width: 100%;
  }

  .ecm .day-schedule-list {
    border: 0;
    background: transparent;
  }

  .ecm .day-schedule-head {
    display: none;
  }

  .ecm .day-schedule-row {
    grid-template-columns: 1fr;
    border: 1px solid var(--tz-line);
    border-radius: 8px;
    margin-bottom: 10px;
    background: #fff;
  }

  .ecm .day-schedule-row .time {
    width: 100%;
  }
}
.ecm .day-schedule-row.is-disabled {
  background: #fafafb;
}

.ecm .day-schedule-row.is-disabled .v-input,
.ecm .day-schedule-row.is-disabled .day-empty {
  opacity: 0.65;
}
.ecm .schedule-error {
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #fff1f1;
  color: #c62828;
  font-size: 13px;
  line-height: 1.35;
}
.ecm .day-schedule-error {
  grid-column: 2 / -1;
  margin-top: -4px;
  padding: 8px 10px;
  border-radius: 6px;
  background: #fff1f1;
  color: #c62828;
  font-size: 12px;
  line-height: 1.3;
}
.ecm .sub-label {
  font: 600 13px/1.25 Manrope, sans-serif;
  color: var(--tz-text);
  margin-bottom: 8px;
}
.ecm .day-schedule-row .day-start-time .v-input__slot {
  min-height: 58px !important;
  height: 58px !important;
}
.ecm .schedule-error--compact {
  margin-top: 8px;
}
</style>
